import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { adminService } from '../../services/adminService';
import { formatDate } from '../../utils/formatters';

const LEDGER_TYPE_LABELS: Record<string, string> = {
  PLATFORM_FEE: 'Platform Fee',
  WITHDRAWAL_FEE: 'Withdrawal Fee',
  AFFILIATE_COMMISSION: 'Affiliate Commission',
};

const LEDGER_TYPE_VARIANTS: Record<string, 'success' | 'info' | 'warning'> = {
  PLATFORM_FEE: 'success',
  WITHDRAWAL_FEE: 'info',
  AFFILIATE_COMMISSION: 'warning',
};

export const AdminFinance: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ledgerType, setLedgerType] = useState('');
  const [ledgerPage, setLedgerPage] = useState(1);

  const dateParams = {
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  };

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['admin', 'finance', 'summary', dateParams],
    queryFn: () => adminService.getFinanceSummary(dateParams),
  });

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['admin', 'finance', 'ledger', { ...dateParams, type: ledgerType, page: ledgerPage, limit: 15 }],
    queryFn: () => adminService.getFinanceLedger({ ...dateParams, ...(ledgerType && { type: ledgerType }), page: ledgerPage, limit: 15 }),
  });

  const summary = summaryData?.data;
  const entries = ledgerData?.data?.entries || [];
  const pagination = ledgerData?.data?.pagination;

  const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (summaryLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Finance</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Revenue tracking and platform ledger</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" />
          <span className="text-gray-400 dark:text-gray-500 text-sm">to</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" />
          {(startDate || endDate) && (
            <button onClick={() => { setStartDate(''); setEndDate(''); }} className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium">Clear</button>
          )}
        </div>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <Card className="border-l-4 border-l-emerald-500 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-50 dark:bg-emerald-900/30 rounded-full -mr-6 -mt-6 opacity-60" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Gross Revenue</p>
              <div className="bg-emerald-100 dark:bg-emerald-900/50 p-2 rounded-xl"><TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /></div>
            </div>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{fmt(summary?.revenue.grossRevenue || 0)}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Platform fees + Withdrawal fees</p>
          </div>
        </Card>
        <Card className="border-l-4 border-l-red-400 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-red-50 dark:bg-red-900/30 rounded-full -mr-6 -mt-6 opacity-60" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Expenses</p>
              <div className="bg-red-100 dark:bg-red-900/50 p-2 rounded-xl"><TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" /></div>
            </div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{fmt(summary?.revenue.affiliateCommissions || 0)}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Affiliate commissions paid</p>
          </div>
        </Card>
        <Card className="border-l-4 border-l-indigo-500 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-50 dark:bg-indigo-900/30 rounded-full -mr-6 -mt-6 opacity-60" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Net Revenue</p>
              <div className="bg-indigo-100 dark:bg-indigo-900/50 p-2 rounded-xl"><DollarSign className="h-5 w-5 text-indigo-600 dark:text-indigo-400" /></div>
            </div>
            <p className={`text-2xl font-bold ${(summary?.revenue.netRevenue || 0) >= 0 ? 'text-indigo-700 dark:text-indigo-400' : 'text-red-600 dark:text-red-400'}`}>
              {fmt(summary?.revenue.netRevenue || 0)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Gross revenue - Expenses</p>
          </div>
        </Card>
      </div>

      {/* Revenue Breakdown + Operations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <Card>
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Revenue Breakdown</h2>
          <div className="space-y-1">
            <div className="flex items-center justify-between py-3 px-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 dark:bg-emerald-900/50 p-1.5 rounded-lg"><ArrowUpRight className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /></div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">Platform Fees</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{summary?.revenue.platformFeeCount || 0} draws</p>
                </div>
              </div>
              <p className="font-semibold text-emerald-700 dark:text-emerald-400">+{fmt(summary?.revenue.platformFees || 0)}</p>
            </div>
            <div className="flex items-center justify-between py-3 px-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 dark:bg-emerald-900/50 p-1.5 rounded-lg"><ArrowUpRight className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /></div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">Withdrawal Fees</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{summary?.revenue.withdrawalFeeCount || 0} withdrawals</p>
                </div>
              </div>
              <p className="font-semibold text-emerald-700 dark:text-emerald-400">+{fmt(summary?.revenue.withdrawalFees || 0)}</p>
            </div>
            <div className="flex items-center justify-between py-3 px-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 dark:bg-red-900/50 p-1.5 rounded-lg"><ArrowDownRight className="h-4 w-4 text-red-500 dark:text-red-400" /></div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">Affiliate Commissions</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{summary?.revenue.affiliateCommissionCount || 0} payouts</p>
                </div>
              </div>
              <p className="font-semibold text-red-600 dark:text-red-400">-{fmt(summary?.revenue.affiliateCommissions || 0)}</p>
            </div>
            <div className="border-t border-gray-100 dark:border-gray-800 mt-2 pt-2">
              <div className="flex items-center justify-between py-3 px-3">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-100 dark:bg-indigo-900/50 p-1.5 rounded-lg"><Minus className="h-4 w-4 text-indigo-600 dark:text-indigo-400" /></div>
                  <p className="font-bold text-gray-900 dark:text-gray-100">Net Revenue</p>
                </div>
                <p className="font-bold text-lg text-gray-900 dark:text-gray-100">{fmt(summary?.revenue.netRevenue || 0)}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Platform Operations</h2>
          <div className="space-y-1">
            {[
              { label: 'Total Ticket Sales', value: fmt(summary?.operations.totalTicketSales || 0) },
              { label: 'Tickets Sold', value: (summary?.operations.totalTicketsSold || 0).toLocaleString() },
              { label: 'Draws Completed', value: String(summary?.operations.totalDraws || 0) },
              { label: 'Draw Sales Volume', value: fmt(summary?.operations.totalDrawSales || 0) },
              { label: 'Organizer Payouts', value: fmt(summary?.operations.totalOrganizerPayouts || 0) },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between py-3 px-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <p className="text-gray-600 dark:text-gray-400">{item.label}</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{item.value}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Platform Ledger</h2>
          <select value={ledgerType} onChange={(e) => { setLedgerType(e.target.value); setLedgerPage(1); }}
            className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors">
            <option value="">All Types</option>
            <option value="PLATFORM_FEE">Platform Fees</option>
            <option value="WITHDRAWAL_FEE">Withdrawal Fees</option>
            <option value="AFFILIATE_COMMISSION">Affiliate Commissions</option>
          </select>
        </div>
        {ledgerLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : entries.length === 0 ? (
          <p className="text-center text-gray-400 dark:text-gray-500 py-12">No ledger entries found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Currency</th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-6 py-4 text-gray-500 dark:text-gray-400">{formatDate(entry.createdAt)}</td>
                    <td className="px-6 py-4">
                      <Badge variant={LEDGER_TYPE_VARIANTS[entry.type] || 'default'}>
                        {LEDGER_TYPE_LABELS[entry.type] || entry.type}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-gray-700 dark:text-gray-300 max-w-xs truncate">{entry.description}</td>
                    <td className="px-6 py-4 text-gray-500 dark:text-gray-400">{entry.currency}</td>
                    <td className={`px-6 py-4 text-right font-semibold ${entry.type === 'AFFILIATE_COMMISSION' ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                      {entry.type === 'AFFILIATE_COMMISSION' ? '-' : '+'}{fmt(parseFloat(entry.amount as any))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4 py-4 border-t border-gray-100 dark:border-gray-800">
            <Button variant="outline" size="sm" disabled={ledgerPage === 1} onClick={() => setLedgerPage(ledgerPage - 1)}>Previous</Button>
            <span className="text-sm text-gray-500 dark:text-gray-400">Page {ledgerPage} of {pagination.pages}</span>
            <Button variant="outline" size="sm" disabled={ledgerPage === pagination.pages} onClick={() => setLedgerPage(ledgerPage + 1)}>Next</Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  );
};
